/**
 * @file
 * RAOC-23: pure translation logic backing the `markUserConnected(source)` API
 * exposed by {@link useRovoConfigStore}.
 *
 * The chat connectors-store action `markUserConnected` is invoked with a
 * connector's chat-namespace `source` key (e.g. `'slack'`, `'google_drive'`).
 * `useRovoConfig`'s `onOAuthSuccess`, however, is keyed on the config-namespace
 * 3P **product key** (the `thirdPartyConfigs` key, e.g. `'slack'`,
 * `'google-drive'`) plus an optional `datasourceId`. This module owns the
 * translation so the hook can stay a thin wiring layer.
 *
 * Kept as a standalone pure function (rather than inline in the hook) so the
 * mapping can be unit-tested in isolation and so the hook body stays sane.
 */

import { type OnOAuthSuccessInput } from '../useRovoConfig';
import { resolveChatSource } from './toChatSourceKey';

/**
 * The shape of a single `thirdPartyConfigs` entry that this resolver depends
 * on. Deliberately the **narrowest** subset of the real
 * `ThirdPartyConfigsWithAPIResponse` needed for source→product translation, so
 * the helper does not couple to the full config type.
 */
type ThirdPartyConfigEntry = {
	resourceType?: string | null;
};

/**
 * `thirdPartyConfigs` map as returned by `useRovoConfig`: a partial record of
 * config-namespace product key → config entry (entries may be absent/null).
 */
export type ThirdPartyConfigsForOAuth = Partial<
	Record<string, ThirdPartyConfigEntry | null | undefined>
>;

/**
 * Translate a chat-namespace connector `source` to the `onOAuthSuccess` input
 * (the config-namespace **product key**) by reverse-indexing `thirdPartyConfigs`
 * with the same {@link resolveChatSource} mapping that {@link useRovoConfigStore}
 * uses to key connectors. This keeps the read projection and the OAuth write on
 * a single, consistent mapping.
 *
 * Deliberately returns a **whole-product** target (no `datasourceId`).
 * `markUserConnected(source)` is a product-level "the user just connected this
 * connector" signal, and the store projects exactly one connector per top-level
 * product key, reading the *top-level* `config.userNeedsOAuth`
 * (`isUserOAuthed: config.userNeedsOAuth !== true`). `onOAuthSuccess` only flips
 * that top-level flag when called **without** a `datasourceId`; passing one
 * instead routes it into the per-datasource branch, which flips a *nested*
 * `datasources[].userNeedsOAuth` that the store never reads — so the connector
 * would stay un-authed in the projected state. We therefore never forward a
 * `datasourceId` here, even though config entries carry one. (Per-datasource
 * OAuth, if ever needed by chat, would require a separate datasource-aware API.)
 *
 * Returns `undefined` for sources that have no matching 3P config — 1P /
 * synthetic connectors (e.g. `help_aggregator`) or 3P products that are not
 * currently configured. Callers should treat `undefined` as a no-op, mirroring
 * `onOAuthSuccess`'s own no-op-on-unknown contract.
 *
 * @param thirdPartyConfigs the `thirdPartyConfigs` map from `useRovoConfig`.
 * @param source the chat-namespace connector source key.
 */
export const resolveOAuthTargetForSource = (
	thirdPartyConfigs: ThirdPartyConfigsForOAuth | undefined,
	source: string,
): OnOAuthSuccessInput | undefined => {
	if (!thirdPartyConfigs) {
		return undefined;
	}

	// TODO RAOC-23 add coverage for bidirectional mapping here (so that
	// smartlinks vs full connectors resolve correctly) (pending branch
	// rwasher/RAOC-23-drive-connector-collapse-fix merging will require a rebase)
	for (const [productKey, config] of Object.entries(thirdPartyConfigs)) {
		if (!config) {
			continue;
		}
		if (resolveChatSource(productKey, config.resourceType) !== source) {
			continue;
		}
		return {
			product: productKey as OnOAuthSuccessInput['product'],
		};
	}

	return undefined;
};
